import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { can, useAuth } from '../lib/auth';
import { COMPANY_ADDRESS, COMPANY_GSTIN, COMPANY_NAME, kgValue, money, poDateText, qtyText, qtyValue, sheetKg } from '../lib/boardPo';
import { readNumber } from '../lib/formula';
import { useVendors } from '../lib/lookups';

/**
 * GRN - Goods Received Note.  One per delivery of raw material (ITEM) or
 * boards (BOARD), made by Purchase: the day the goods came in, who sent them,
 * the PO, invoice and challan, and a line per item / board.  Each line books
 * the stock in, dated on the day it came in - so a delivery that came on a
 * Sunday and is entered on Monday counts as Sunday's.  A received date before
 * today (up to 7 days back) needs a reason.
 *
 * GrnTab lists them; GrnForm makes one (opened from "+ In" / "+ Receive" or
 * a PO line with that PO's due lines on it) or edits one (Purchase for 7 days
 * after entering it, Super Admin any time - and Cancel GRN); GrnPrintPage
 * prints the slip.
 */

export type GrnKind = 'ITEM' | 'BOARD';

/** What a GRN form can open with: an item or board on its first line, a PO. */
export interface GrnStart {
  itemCode?: string;
  paperStockId?: number;
  poNumber?: string;
  qty?: number;
  /** Per the item's unit (ITEM); per sheet for a board line bought in sheets. */
  rate?: number | null;
  /** Per kg, for a board. */
  ratePerKg?: number | null;
}

interface ItemOpt { itemCode: string; sku: string | null; itemName: string; itemGroup: string | null; uom: string | null; category: string; standardCost: number | null }
interface BoardOpt { id: number; sku: string; paperType: string; lengthIn: number | null; widthIn: number | null; gsm: number | null; brand: string; rate: number | null; location: string }
interface PoLine {
  paperStockId: number | null; itemCode: string | null; qty: number; uom: string; rate: number;
  receivedQty: number; receivedSheets: number;
}
interface Po { id: number; poNumber: string; vendorName: string; status: string; lines: PoLine[] }

interface GrnListRow {
  id: number; grnNo: string; kind: GrnKind; receivedOn: string; vendorName: string; poNumber: string;
  invoiceNumber: string; invoiceDate: string | null; challanNo: string; backdateReason: string;
  enteredOn: string; enteredBy: string; lines: number; totalValue: number; items: string;
  editedOn: string | null; editedBy: string; cancelledOn: string | null; cancelledBy: string; cancelReason: string;
  /** This user may edit / cancel it now. */
  editable: boolean;
}

const todayIst = () => new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
const daysBack = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
const r2 = (n: number) => Math.round(n * 100) / 100;
const BACKDATE_DAYS = 7;
const isCut = (sku: string) => sku.trim().toUpperCase().startsWith('CUT');
const itemLabel = (i: ItemOpt) => `${i.sku || i.itemCode} · ${i.itemName}`;
const boardLabel = (b: BoardOpt) =>
  `${b.sku} · ${[b.paperType, b.lengthIn != null && b.widthIn != null ? `${b.lengthIn} x ${b.widthIn}` : '', b.gsm ? `${b.gsm} GSM` : '', b.brand].filter(Boolean).join(' · ')}`;

function grnError(m: string): string {
  if (m === 'backdate_reason_required') return 'The goods came in before today - write why it is entered late.';
  if (m.startsWith('received_too_old')) return `The received date can be at most ${m.split(':')[1] ?? BACKDATE_DAYS} days back.`;
  if (m === 'received_in_future') return 'The received date cannot be after today.';
  if (m === 'grn_migration_pending') return 'GRN needs the server database update first.';
  if (m.startsWith('item_not_found')) return `An item on a line was not found (${m.split(':')[1]}).`;
  if (m.startsWith('board_not_found')) return 'A board on a line was not found.';
  if (m.startsWith('cut_stock_not_received')) return `${m.split(':')[1]} is cutting stock - it is never bought, so it cannot be on a GRN.`;
  if (m === 'forbidden') return 'Only Purchase and Super Admin can make or edit a GRN.';
  if (m.startsWith('grn_edit_window_passed')) return `A GRN can be edited for ${m.split(':')[1] ?? 7} days after it is entered - ask Super Admin.`;
  if (m === 'grn_cancelled') return 'This GRN is cancelled - it cannot be changed.';
  if (m === 'grn_edit_migration_pending') return 'Editing a GRN needs the server database update first (migration_2026_11_03_grn_edit.sql).';
  if (m.startsWith('grn_line_not_found')) return 'A line of this GRN changed meanwhile - close and open it again.';
  if (m.startsWith('grn_stock_would_go_negative')) {
    const [, sku, left] = m.split(':');
    return `${sku}: only ${left} in stock now (some was already issued) - the GRN cannot take off more than that.`;
  }
  return m;
}

// ------------------------------------------------------------------- list

export function GrnTab({ kind, categories }: { kind: GrnKind; categories?: string[] }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canMake = can(user, 'MANAGE_INVENTORY');
  const today = todayIst();
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState('');
  const [making, setMaking] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const list = useQuery({
    queryKey: ['grn-list', kind, from, to, q.trim()],
    queryFn: () => api<{ ready: boolean; rows: GrnListRow[] }>(
      `/api/grn?kind=${kind}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`),
    placeholderData: (prev) => prev,
  });
  const rows = list.data?.rows ?? [];
  const total = rows.reduce((p, r) => p + r.totalValue, 0);
  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="label">Received from</label><input type="date" className="input !w-auto" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><label className="label">To</label><input type="date" className="input !w-auto" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <div className="flex-1 min-w-[220px]">
          <label className="label">Search</label>
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="GRN no., vendor, PO, invoice, challan, item or SKU" />
        </div>
        <div className="pb-2 text-sm text-slate-600">{rows.length} GRN(s) · <b className="text-amber-700">₹{money(total)}</b></div>
        {canMake && <button type="button" className="btn-primary bg-emerald-600 hover:bg-emerald-700" onClick={() => setMaking(true)}>+ New GRN</button>}
      </div>
      {list.data && !list.data.ready && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">GRN needs the server database update (migration_2026_11_02_grn.sql).</div>
      )}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">GRN No.</th>
              <th className="px-3 py-2 text-left">Received</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-left">PO · Invoice · Challan</th>
              <th className="px-3 py-2 text-left">{kind === 'BOARD' ? 'Boards' : 'Items'}</th>
              <th className="px-3 py-2 text-right">Value (₹)</th>
              <th className="px-3 py-2 text-left">Entered</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={8} className="p-4 text-center text-slate-500">Loading…</td></tr>}
            {list.isError && <tr><td colSpan={8} className="p-4 text-center text-red-600">Could not load the GRNs.</td></tr>}
            {!list.isLoading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-400">No GRN in these dates.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 align-top hover:bg-yellow-50 ${r.cancelledOn ? 'opacity-60' : ''}`}>
                <td className="px-3 py-2 font-mono text-xs font-bold text-brand whitespace-nowrap">
                  <span className={r.cancelledOn ? 'line-through' : ''}>{r.grnNo}</span>
                  {r.cancelledOn && (
                    <div className="mt-1 font-sans font-semibold text-red-700" title={r.cancelReason}>
                      Cancelled<span className="block text-[11px] font-normal">{r.cancelReason}</span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {poDateText(r.receivedOn)}
                  {r.backdateReason && <div className="text-[11px] text-amber-700" title={r.backdateReason}>entered late: {r.backdateReason}</div>}
                </td>
                <td className="px-3 py-2">{r.vendorName}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {r.poNumber && <div>PO {r.poNumber}</div>}
                  {(r.invoiceNumber || r.invoiceDate) && <div>Inv {r.invoiceNumber || '—'}{r.invoiceDate ? ` · ${poDateText(r.invoiceDate)}` : ''}</div>}
                  {r.challanNo && <div>Challan {r.challanNo}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-700 max-w-[320px]">
                  <span className="font-semibold">{r.lines}</span> · {r.items}{r.lines > 6 ? '…' : ''}
                </td>
                <td className="px-3 py-2 text-right font-bold text-amber-700 whitespace-nowrap">{money(r.totalValue)}</td>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {r.enteredBy}<div>{r.enteredOn.slice(0, 16)}</div>
                  {r.editedOn && <div className="text-[11px] text-sky-700">edited {r.editedOn.slice(0, 16)}{r.editedBy ? ` · ${r.editedBy}` : ''}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  {r.editable && <button type="button" className="mr-3 font-medium text-brand hover:underline" onClick={() => setEditing(r.id)}>Edit</button>}
                  <Link to={`/grn/${r.id}/print`} target="_blank" className="text-slate-600 hover:underline">Print</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {making && (
        <GrnForm kind={kind} categories={categories} onClose={() => setMaking(false)}
          onSaved={() => { void qc.invalidateQueries({ queryKey: ['grn-list'] }); }} />
      )}
      {editing != null && (
        <GrnForm kind={kind} categories={categories} editId={editing} onClose={() => setEditing(null)}
          onSaved={() => { void qc.invalidateQueries({ queryKey: ['grn-list'] }); }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------- form

/** id: the line's movement, when editing a GRN (absent on a new line). */
interface Line { key: number; id?: number; label: string; qtyText: string; rateText: string; weightText: string }

export function GrnForm({ kind, categories, start, editId, onClose, onSaved }: {
  kind: GrnKind;
  /** ITEM: the page's categories - the items a line may be. */
  categories?: string[];
  start?: GrnStart;
  /** Edit this GRN instead of making a new one. */
  editId?: number;
  onClose: () => void;
  /** Stock changed: the page refreshes.  The form stays open on its result. */
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const today = todayIst();
  const vendors = useVendors();
  const editing = editId != null;
  const docQ = useQuery({
    enabled: editing,
    queryKey: ['grn', String(editId)],
    queryFn: () => api<GrnDoc>(`/api/grn/${editId}`),
    staleTime: 0,
  });
  // A received date may be up to BACKDATE_DAYS before the day the GRN was entered.
  const enteredDay = editing ? docQ.data?.enteredDay ?? today : today;
  const minDay = daysBack(enteredDay, BACKDATE_DAYS);

  const itemsQ = useQuery({
    enabled: kind === 'ITEM',
    queryKey: ['inv-items'],
    queryFn: () => api<{ rows: ItemOpt[] }>('/api/inventory/items'),
  });
  const boardsQ = useQuery({
    enabled: kind === 'BOARD',
    queryKey: ['paper-stock', 'grn-picker'],
    queryFn: () => api<{ rows: BoardOpt[] }>('/api/paper-stock?sort=sku'),
  });
  const items = useMemo(() => (itemsQ.data?.rows ?? []).filter((i) => !categories || categories.includes(i.category)), [itemsQ.data, categories]);
  const boards = useMemo(() => (boardsQ.data?.rows ?? []).filter((b) => !isCut(b.sku)), [boardsQ.data]);
  const itemBy = useMemo(() => new Map(items.map((i) => [itemLabel(i), i])), [items]);
  const boardBy = useMemo(() => new Map(boards.map((b) => [boardLabel(b), b])), [boards]);

  const posQ = useQuery({
    queryKey: ['grn-open-pos', kind],
    queryFn: () => api<{ rows: Po[] }>(`/api/board-po?kind=${kind === 'BOARD' ? 'BOARD' : 'RAW_MATERIAL'}`),
    retry: false,
  });
  const openPos = (posQ.data?.rows ?? []).filter((p) => p.status === 'OPEN' || p.status === 'PARTIAL');

  const [receivedOn, setReceivedOn] = useState(today);
  const [reason, setReason] = useState('');
  const [vendor, setVendor] = useState('');
  const [poNumber, setPoNumber] = useState(start?.poNumber ?? '');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [challanNo, setChallanNo] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<Line[]>([{ key: 1, label: '', qtyText: start?.qty ? String(start.qty) : '', rateText: '', weightText: '' }]);
  const [startApplied, setStartApplied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ id: number; grnNo: string; cancelled?: boolean } | null>(null);
  const [editApplied, setEditApplied] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const perKgFor = (b: BoardOpt | undefined, rate: number, uom: string): number | null => {
    if (!(rate > 0)) return null;
    if (uom === 'KG') return r2(rate);
    if (uom === 'TON') return r2(rate / 1000);
    const kgs = sheetKg(b ?? null);
    return uom === 'SHEETS' && kgs > 0 ? r2(rate / kgs) : null;
  };

  // Editing: the GRN as it stands, once it and the lists are in.
  if (editing && !editApplied && docQ.data && (kind === 'ITEM' ? items.length : boards.length)) {
    setEditApplied(true);
    const g = docQ.data;
    setReceivedOn(g.receivedOn);
    setReason(g.backdateReason ?? '');
    setVendor(g.vendorName ?? '');
    setPoNumber(g.poNumber ?? '');
    setInvoiceNumber(g.invoiceNumber ?? '');
    setInvoiceDate(g.invoiceDate ?? '');
    setChallanNo(g.challanNo ?? '');
    setVehicleNo(g.vehicleNo ?? '');
    setRemarks(g.remarks ?? '');
    let key = 1;
    setLines(g.lines.filter((l) => !l.reversed).map((l) => {
      const it = kind === 'ITEM' ? items.find((i) => i.itemCode === l.itemCode) : undefined;
      const b = kind === 'BOARD' ? boards.find((x) => x.id === l.paperStockId) : undefined;
      return {
        key: key++, id: l.id,
        label: it ? itemLabel(it) : b ? boardLabel(b) : `${l.sku} · ${l.name}`,
        qtyText: String(l.qty), rateText: l.rate != null ? String(l.rate) : '',
        weightText: l.weightKg != null ? String(l.weightKg) : '',
      };
    }));
  }

  // The item / board the form was opened for, once the lists are in.  Opened
  // from a PO line: everything still due on that PO, that line first - one
  // GRN takes the whole delivery.
  const posReady = !start?.poNumber || posQ.isFetched;
  if (!editing && !startApplied && start && posReady && (kind === 'ITEM' ? items.length : boards.length)) {
    setStartApplied(true);
    const po = start.poNumber ? openPos.find((x) => x.poNumber === start.poNumber) : undefined;
    const due = po ? dueLinesOf(po) : [];
    if (due.length) {
      const it = kind === 'ITEM' ? items.find((i) => i.itemCode === start.itemCode) : undefined;
      const b = kind === 'BOARD' ? boards.find((x) => x.id === start.paperStockId) : undefined;
      const first = it ? itemLabel(it) : b ? boardLabel(b) : null;
      setLines([...due.filter((l) => l.label === first), ...due.filter((l) => l.label !== first)]);
    } else if (kind === 'ITEM' && start.itemCode) {
      const it = items.find((i) => i.itemCode === start.itemCode);
      if (it) {
        const rate = start.rate != null && start.rate > 0 ? start.rate : it.standardCost;
        setLines([{ key: 1, label: itemLabel(it), qtyText: start.qty ? String(start.qty) : '', rateText: rate ? String(rate) : '', weightText: '' }]);
      }
    }
    if (kind === 'BOARD' && start.paperStockId) {
      const b = boards.find((x) => x.id === start.paperStockId);
      if (b) {
        const perKg = start.ratePerKg && start.ratePerKg > 0 ? r2(start.ratePerKg)
          : start.rate && start.rate > 0 ? perKgFor(b, start.rate, 'SHEETS') : b.rate;
        setLines([{ key: 1, label: boardLabel(b), qtyText: start.qty ? String(start.qty) : '', rateText: perKg ? String(perKg) : '', weightText: '' }]);
      }
    }
  }
  // Opened from a PO line: that PO's vendor, once the POs are in.
  useEffect(() => {
    if (!start?.poNumber || vendor) return;
    const po = openPos.find((p) => p.poNumber === start.poNumber);
    if (po?.vendorName) setVendor(po.vendorName.trim());
  }, [posQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Picking a PO: its vendor, and a line for everything still due on it. */
  function pickPo(n: string) {
    setPoNumber(n);
    const po = openPos.find((p) => p.poNumber === n);
    if (!po) return;
    if (po.vendorName?.trim()) setVendor(po.vendorName.trim());
    const due = dueLinesOf(po);
    if (due.length) setLines(due);
  }

  /** A line for everything still due on a PO, at the PO's rates. */
  function dueLinesOf(po: Po): Line[] {
    const due: Line[] = [];
    let key = Date.now();
    for (const l of po.lines) {
      if (kind === 'ITEM' && l.itemCode) {
        const it = items.find((i) => i.itemCode === l.itemCode);
        const left = Math.max(0, l.qty - l.receivedQty);
        if (it && left > 0) due.push({ key: key++, label: itemLabel(it), qtyText: String(left), rateText: l.rate > 0 ? String(l.rate) : '', weightText: '' });
      }
      if (kind === 'BOARD' && l.paperStockId) {
        const b = boards.find((x) => x.id === l.paperStockId);
        const left = l.uom === 'SHEETS' ? Math.max(0, l.qty - l.receivedSheets) : 0;
        const perKg = perKgFor(b, l.rate, l.uom);
        if (b && (l.uom !== 'SHEETS' || left > 0)) due.push({ key: key++, label: boardLabel(b), qtyText: left ? String(left) : '', rateText: perKg ? String(perKg) : '', weightText: '' });
      }
    }
    return due;
  }

  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { key: Date.now(), label: '', qtyText: '', rateText: '', weightText: '' }]);
  const dropLine = (key: number) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls));

  // Each line as read from what is typed.
  const read = lines.map((l) => {
    const qty = readNumber(l.qtyText);
    const rate = readNumber(l.rateText);
    if (kind === 'ITEM') {
      const it = itemBy.get(l.label);
      return { l, it, b: undefined, qty, rate, weight: null as number | null, value: qty && qty > 0 && rate && rate > 0 ? qtyValue(qty, rate) : 0 };
    }
    const b = boardBy.get(l.label);
    const typedKg = readNumber(l.weightText);
    const autoKg = qty && qty > 0 ? r2(qty * sheetKg(b ?? null)) : null;
    const weight = typedKg ?? (autoKg && autoKg > 0 ? autoKg : null);
    return { l, it: undefined, b, qty, rate, weight, value: weight && rate && rate > 0 ? kgValue(weight, rate) : 0 };
  });
  const total = read.reduce((p, r) => p + r.value, 0);
  const backdated = receivedOn < enteredDay;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => (editing
      ? api<{ ok: true; id: number }>(`/api/grn/${editId}`, { method: 'PUT', body: JSON.stringify(body) })
        .then((r) => ({ ...r, grnNo: docQ.data?.grnNo ?? '' }))
      : api<{ ok: true; id: number; grnNo: string }>('/api/grn', { method: 'POST', body: JSON.stringify(body) })),
    onSuccess: (r) => {
      setSaved({ id: r.id, grnNo: r.grnNo });
      void qc.invalidateQueries({ queryKey: ['grn'] });
      void qc.invalidateQueries({ queryKey: ['grn-list'] });
      void qc.invalidateQueries({ queryKey: ['grn-open-pos'] });
      onSaved();
    },
    onError: (e) => setErr(grnError((e as Error).message)),
  });

  const cancel = useMutation({
    mutationFn: () => api<{ ok: true }>(`/api/grn/${editId}/cancel`, { method: 'POST', body: JSON.stringify({ reason: cancelReason.trim() }) }),
    onSuccess: () => {
      setSaved({ id: editId!, grnNo: docQ.data?.grnNo ?? '', cancelled: true });
      void qc.invalidateQueries({ queryKey: ['grn-list'] });
      void qc.invalidateQueries({ queryKey: ['grn'] });
      void qc.invalidateQueries({ queryKey: ['grn-open-pos'] });
      onSaved();
    },
    onError: (e) => setErr(grnError((e as Error).message)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!receivedOn || receivedOn > today) { setErr('The received date cannot be after today.'); return; }
    if (receivedOn < minDay) { setErr(`The received date can be at most ${BACKDATE_DAYS} days back.`); return; }
    if (backdated && reason.trim().length < 3) { setErr(`The goods came in before ${editing ? 'the day this GRN was entered' : 'today'} - write why it is entered late.`); return; }
    if (!vendor.trim()) { setErr('Enter who the goods came from.'); return; }
    const body: Record<string, unknown>[] = [];
    for (const [n, r] of read.entries()) {
      const at = `Line ${n + 1}`;
      if (kind === 'ITEM' ? !r.it : !r.b) { setErr(`${at}: pick the ${kind === 'ITEM' ? 'item' : 'board'} from the list.`); return; }
      if (r.qty == null || Number.isNaN(r.qty) || !(r.qty > 0)) { setErr(`${at}: enter the quantity received.`); return; }
      if (r.rate == null || Number.isNaN(r.rate) || !(r.rate > 0)) { setErr(`${at}: enter the rate${kind === 'BOARD' ? ' per kg' : ''}.`); return; }
      if (kind === 'ITEM') body.push({ id: r.l.id ?? null, itemCode: r.it!.itemCode, qty: Math.round(r.qty * 10000) / 10000, rate: r.rate });
      else {
        if (Math.abs(r.qty - Math.round(r.qty)) > 1e-6) { setErr(`${at}: sheets must be a whole number.`); return; }
        body.push({ id: r.l.id ?? null, paperStockId: r.b!.id, sheets: Math.round(r.qty), weightKg: r.weight ?? null, ratePerKg: r.rate });
      }
    }
    save.mutate({
      kind, receivedOn, backdateReason: backdated ? reason.trim() : null, vendorName: vendor.trim(),
      poNumber: poNumber.trim() || null, invoiceNumber: invoiceNumber.trim() || null, invoiceDate: invoiceDate || null,
      challanNo: challanNo.trim() || null, vehicleNo: vehicleNo.trim() || null, remarks: remarks.trim() || null,
      lines: body,
    });
  }

  if (saved) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 text-center">
          <div className={`text-lg font-semibold ${saved.cancelled ? 'text-red-700' : 'text-emerald-700'}`}>
            {saved.cancelled ? 'GRN cancelled' : editing ? '✓ GRN updated' : '✓ GRN saved'}
          </div>
          <div className="mt-2 font-mono text-xl font-bold text-brand">{saved.grnNo}</div>
          <div className="mt-1 text-sm text-slate-600">
            {saved.cancelled ? 'Every line was reversed - the stock is back as before.' : `Stock booked in, dated ${poDateText(receivedOn)}.`}
          </div>
          <div className="mt-5 flex justify-center gap-2">
            <a href={`/grn/${saved.id}/print`} target="_blank" rel="noreferrer" className="btn-primary">Print GRN</a>
            <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const listId = `grn-pick-${kind}`;
  if (editing && (docQ.isLoading || (docQ.data && !editApplied))) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl p-6 text-slate-600">Loading the GRN…</div>
      </div>
    );
  }
  if (editing && docQ.isError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl p-6 text-center">
          <div className="text-red-700">Could not load the GRN.</div>
          <button type="button" className="btn-ghost mt-3" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full mx-4 max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        {editing ? (
          <>
            <h2 className="text-lg font-semibold text-brand">Edit <span className="font-mono">{docQ.data?.grnNo}</span></h2>
            <p className="mb-4 text-xs text-slate-500">
              Entered {docQ.data?.enteredOn.slice(0, 16)} by {docQ.data?.enteredBy}. A changed quantity or item is reversed and booked again, so History keeps both.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold text-emerald-700">New GRN — {kind === 'BOARD' ? 'boards' : 'raw material'} received</h2>
            <p className="mb-4 text-xs text-slate-500">One GRN per delivery. Stock is booked in on the day it came in.</p>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
          <div>
            <label className="label">Received on *</label>
            <input type="date" className="input" value={receivedOn} min={minDay} max={today} onChange={(e) => setReceivedOn(e.target.value)} />
          </div>
          {backdated ? (
            <div className="sm:col-span-2">
              <label className="label">Why entered late? *</label>
              <input className="input" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. came on Sunday, office closed" />
            </div>
          ) : <div className="hidden sm:block sm:col-span-2" />}
          <div className="sm:col-span-2">
            <label className="label">Vendor *</label>
            <input className="input" list="grn-vendors" value={vendor} maxLength={200} onChange={(e) => setVendor(e.target.value)} placeholder="Who it came from — pick or type" />
            <datalist id="grn-vendors">{(vendors.data ?? []).map((v) => <option key={v.id} value={v.name.trim()} />)}</datalist>
          </div>
          <div>
            <label className="label">Against PO</label>
            <select className="input" value={openPos.some((p) => p.poNumber === poNumber) ? poNumber : ''} onChange={(e) => pickPo(e.target.value)}>
              <option value="">{poNumber && !openPos.some((p) => p.poNumber === poNumber) ? poNumber : '— No PO —'}</option>
              {openPos.map((p) => <option key={p.id} value={p.poNumber}>{p.poNumber} · {p.vendorName}</option>)}
            </select>
          </div>
          <div><label className="label">Invoice No.</label><input className="input" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} /></div>
          <div><label className="label">Invoice Date</label><input type="date" className="input" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} /></div>
          <div><label className="label">Challan No.</label><input className="input" value={challanNo} onChange={(e) => setChallanNo(e.target.value)} /></div>
          <div><label className="label">Vehicle No.</label><input className="input" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} placeholder="Optional" /></div>
          <div className="sm:col-span-2"><label className="label">Remarks</label><input className="input" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional" /></div>
        </div>

        <div className="mt-5 overflow-x-auto rounded border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-2 py-2 text-left w-8">#</th>
                <th className="px-2 py-2 text-left">{kind === 'BOARD' ? 'Board' : 'Item'}</th>
                <th className="px-2 py-2 text-right w-28">{kind === 'BOARD' ? 'Sheets' : 'Qty'}</th>
                {kind === 'BOARD' && <th className="px-2 py-2 text-right w-28">Weight (kg)</th>}
                <th className="px-2 py-2 text-right w-28">{kind === 'BOARD' ? 'Rate / kg' : 'Rate'}</th>
                <th className="px-2 py-2 text-right w-28">Value (₹)</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {read.map((r, n) => {
                const unit = kind === 'ITEM' ? r.it?.uom ?? '' : 'sheets';
                return (
                  <tr key={r.l.key} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-slate-400">{n + 1}</td>
                    <td className="px-2 py-1.5">
                      <input className="input text-sm" list={listId} value={r.l.label} placeholder={`Type SKU or name, pick from the list`}
                        onChange={(e) => {
                          const label = e.target.value;
                          const patch: Partial<Line> = { label };
                          // A fresh pick takes the item's / board's last rate as a start.
                          if (!r.l.rateText) {
                            const rate = kind === 'ITEM' ? itemBy.get(label)?.standardCost : boardBy.get(label)?.rate;
                            if (rate) patch.rateText = String(rate);
                          }
                          setLine(r.l.key, patch);
                        }} />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex items-center gap-1">
                        <input className="input text-sm text-right" inputMode="decimal" value={r.l.qtyText} onChange={(e) => setLine(r.l.key, { qtyText: e.target.value })} />
                        <span className="text-[11px] text-slate-500 w-10">{unit}</span>
                      </div>
                    </td>
                    {kind === 'BOARD' && (
                      <td className="px-2 py-1.5">
                        <input className="input text-sm text-right" inputMode="decimal" value={r.l.weightText}
                          placeholder={r.weight != null && !r.l.weightText ? `Auto ${qtyText(r.weight)}` : ''}
                          onChange={(e) => setLine(r.l.key, { weightText: e.target.value })} />
                      </td>
                    )}
                    <td className="px-2 py-1.5">
                      <input className="input text-sm text-right" inputMode="decimal" value={r.l.rateText} onChange={(e) => setLine(r.l.key, { rateText: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-700 whitespace-nowrap">{r.value ? money(r.value) : '—'}</td>
                    <td className="px-1 py-1.5 text-center">
                      {lines.length > 1 && <button type="button" className="text-slate-400 hover:text-red-600" title="Remove line" onClick={() => dropLine(r.l.key)}>✕</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td colSpan={kind === 'BOARD' ? 5 : 4} className="px-2 py-2">
                  <button type="button" className="text-sm font-medium text-brand hover:underline" onClick={addLine}>+ Add line</button>
                </td>
                <td className="px-2 py-2 text-right font-bold text-amber-700 whitespace-nowrap">₹{money(total)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          <datalist id={listId}>
            {kind === 'ITEM'
              ? items.map((i) => <option key={i.itemCode} value={itemLabel(i)} />)
              : boards.map((b) => <option key={b.id} value={boardLabel(b)} />)}
          </datalist>
        </div>

        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        {editing && cancelOpen && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 p-3">
            <div className="text-sm font-medium text-red-800">Cancel this GRN? Every line is reversed and the stock goes back down.</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <input className="input flex-1 min-w-[220px] text-sm" value={cancelReason} maxLength={300} onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why - e.g. entered twice, booked in GRN/2026-27/0002" />
              <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" disabled={cancel.isPending || cancelReason.trim().length < 3}
                onClick={() => { setErr(null); cancel.mutate(); }}>
                {cancel.isPending ? 'Cancelling…' : 'Cancel GRN'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setCancelOpen(false)}>Keep it</button>
            </div>
          </div>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          {editing && !cancelOpen && (
            <button type="button" className="mr-auto text-sm font-medium text-red-700 hover:underline" onClick={() => setCancelOpen(true)}>Cancel GRN…</button>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
          <button type="submit" className="btn-primary bg-emerald-600 hover:bg-emerald-700" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Save GRN'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ print

interface GrnDoc extends Omit<GrnListRow, 'lines'> {
  vehicleNo: string; remarks: string;
  lineCount: number;
  enteredDay: string;
  lines: { id: number; itemCode?: string; paperStockId?: number; sku: string; name: string; group: string; uom: string; qty: number;
    weightKg?: number | null; rate: number | null; value: number; reversed: boolean }[];
}

export function GrnPrintPage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['grn', id], queryFn: () => api<GrnDoc>(`/api/grn/${encodeURIComponent(id)}`) });
  if (q.isLoading) return <div style={{ padding: 24, color: '#666' }}>Loading…</div>;
  if (q.isError || !q.data) return <div style={{ padding: 24, color: 'red' }}>Could not load the GRN.</div>;
  const g = q.data;
  const board = g.kind === 'BOARD';
  const lines = g.lines.filter((l) => !l.reversed);
  const cell: CSSProperties = { border: '1px solid #000', padding: '4px 6px', fontSize: 12, verticalAlign: 'top' };
  const num: CSSProperties = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };
  const label: CSSProperties = { ...cell, fontWeight: 'bold', width: '18%', background: '#f3f4f6' };
  return (
    <>
      <style>{'@media print { .no-print { display: none !important } body { background: #fff } } @page { size: A4; margin: 10mm }'}</style>
      <div className="no-print" style={{ padding: '10px 16px', display: 'flex', gap: 8, background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
        <button type="button" className="btn-primary" onClick={() => window.print()}>Print / Save as PDF</button>
        <button type="button" className="btn-ghost" onClick={() => window.close()}>Close</button>
      </div>
      <div style={{ width: '190mm', margin: '12px auto', fontFamily: 'Calibri, Carlito, Arial, sans-serif', color: '#000', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '2px solid #000' }}>
          <tbody>
            <tr><td colSpan={4} style={{ ...cell, textAlign: 'center', padding: '6px' }}>
              <div style={{ fontWeight: 'bold', fontSize: 15 }}>{COMPANY_NAME}</div>
              <div style={{ fontSize: 10 }}>{COMPANY_ADDRESS.join(', ')} · GSTIN : {COMPANY_GSTIN}</div>
            </td></tr>
            <tr><td colSpan={4} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', fontSize: 16, letterSpacing: 3 }}>
              GOODS RECEIVED NOTE{board ? ' — BOARD' : ''}
            </td></tr>
            {g.cancelledOn && (
              <tr><td colSpan={4} style={{ ...cell, textAlign: 'center', fontWeight: 'bold', color: '#b91c1c' }}>
                CANCELLED {g.cancelledOn.slice(0, 16)}{g.cancelledBy ? ` by ${g.cancelledBy}` : ''} — {g.cancelReason}
              </td></tr>
            )}
            <tr>
              <td style={label}>GRN No.</td><td style={{ ...cell, fontWeight: 'bold', fontSize: 14 }}>{g.grnNo}</td>
              <td style={label}>Received On</td><td style={{ ...cell, fontWeight: 'bold', fontSize: 14 }}>{poDateText(g.receivedOn)}</td>
            </tr>
            <tr>
              <td style={label}>Vendor</td><td style={cell}>{g.vendorName}</td>
              <td style={label}>PO No.</td><td style={cell}>{g.poNumber || '—'}</td>
            </tr>
            <tr>
              <td style={label}>Invoice No.</td><td style={cell}>{g.invoiceNumber || '—'}</td>
              <td style={label}>Invoice Date</td><td style={cell}>{g.invoiceDate ? poDateText(g.invoiceDate) : '—'}</td>
            </tr>
            <tr>
              <td style={label}>Challan No.</td><td style={cell}>{g.challanNo || '—'}</td>
              <td style={label}>Vehicle No.</td><td style={cell}>{g.vehicleNo || '—'}</td>
            </tr>
            <tr>
              <td style={label}>Entered</td>
              <td colSpan={3} style={cell}>
                {g.enteredOn.slice(0, 16)} by {g.enteredBy}
                {g.backdateReason && <> — entered late: {g.backdateReason}</>}
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, border: '2px solid #000' }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={cell}>#</th><th style={cell}>SKU</th><th style={{ ...cell, textAlign: 'left' }}>{board ? 'Board' : 'Item'}</th>
              <th style={num}>{board ? 'Sheets' : 'Qty'}</th>{!board && <th style={cell}>Unit</th>}
              {board && <th style={num}>Weight (kg)</th>}
              <th style={num}>{board ? 'Rate / kg' : 'Rate'}</th><th style={num}>Value (₹)</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, n) => (
              <tr key={l.id}>
                <td style={cell}>{n + 1}</td>
                <td style={{ ...cell, fontFamily: 'monospace' }}>{l.sku}</td>
                <td style={cell}>{l.name}{!board && l.group ? <span style={{ color: '#555' }}> · {l.group}</span> : null}</td>
                <td style={num}>{qtyText(l.qty)}</td>{!board && <td style={cell}>{l.uom}</td>}
                {board && <td style={num}>{l.weightKg != null ? qtyText(l.weightKg) : ''}</td>}
                <td style={num}>{l.rate != null ? money(l.rate) : ''}</td>
                <td style={num}>{money(l.value)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={board ? 6 : 6} style={{ ...cell, textAlign: 'right', fontWeight: 'bold' }}>Total</td>
              <td style={{ ...num, fontWeight: 'bold' }}>₹{money(g.totalValue)}</td>
            </tr>
          </tbody>
        </table>
        {g.lines.some((l) => l.reversed) && (
          <div style={{ fontSize: 11, marginTop: 4 }}>Reversed lines are left off ({g.lines.filter((l) => l.reversed).length}).</div>
        )}
        {g.remarks && <div style={{ fontSize: 12, marginTop: 6 }}><b>Remarks:</b> {g.remarks}</div>}

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 40 }}>
          <tbody>
            <tr>
              {['Received by (Store)', 'Checked by', 'Purchase', 'Accounts'].map((s) => (
                <td key={s} style={{ width: '25%', padding: '0 8px', textAlign: 'center', fontSize: 12 }}>
                  <div style={{ borderTop: '1px solid #000', paddingTop: 4 }}>{s}</div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
